import FullScreenLoading from 'layout/widgets/FullScreenLoading'
import { WithPermissions } from 'ra-core'
import React, { Suspense, useMemo } from 'react'
import { Route, RouteProps } from 'react-router-dom'

export type LazyRouteProps = Omit<RouteProps, 'render' | 'component'> &
  (
    | {
        render: RouteProps['render']
      }
    | {
        component: RouteProps['component']
      }
    | { lazy: () => Promise<{ default: any }>; loading?: React.ReactNode }
  ) & {
    children?: React.ReactNode
    noAuth?: boolean
  }

/**
 * Either supply standard react-router render function, a component class, or a lazy factory function like this:
 * ```jsx
 * <LazyRoute lazy={() => () => import('./EditorComponent')} />
 * ```
 */

export const LazyRoute = (props: LazyRouteProps) => {
  const component = props['component']
  const lazy = props['lazy']
  const loading = props['loading']
  const noAuth = props['noAuth']
  const render = props['render']

  const realComponent = useMemo(() => {
    if (component) return component
    if (lazy) return React.lazy(lazy)
    return undefined
  }, [component || lazy])

  if (!noAuth) {
    return (
      <Suspense fallback={loading || <FullScreenLoading />}>
        <Route
          {...props}
          render={(allProps) => {
            return (
              <WithPermissions
                authParams={{ permission: 'is_pro' }}
                component={realComponent}
                render={render}
                {...allProps}
              />
            )
          }}
        />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={loading || <FullScreenLoading />}>
      <Route {...props} component={realComponent} />
    </Suspense>
  )
}
